/**
 * Chart wrappers — minimal SVG-based charts.
 *
 * Built without an external charting library (Recharts/Chart.js) to keep
 * the bundle small. The three shapes we need (donut, horizontal bars,
 * stacked bar) are simple enough to draw directly.
 *
 * If we outgrow this — clickable segments, animations, complex tooltips
 * — swap to Recharts. The component API here exposes `data` as plain
 * arrays so the swap is straightforward.
 */

import { cn } from "../../lib/cn";
import { formatCurrency, formatPercent } from "../../lib/format";

export interface ChartDatum {
  /** Display label. */
  label: string;
  /** Raw numeric value (kr). */
  value: number;
  /** Optional explicit color (hex). Falls back to palette by index. */
  color?: string | undefined;
}

const PALETTE = [
  "#6366f1", // accent (indigo)
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#84cc16", // lime
  "#71717a", // ink
];

function colorAt(i: number, override?: string): string {
  if (override) return override;
  return PALETTE[i % PALETTE.length] ?? PALETTE[0] ?? "#94a3b8";
}

// ---------------------------------------------------------------------------
// DonutChart — proportional donut with center label
// ---------------------------------------------------------------------------

export interface DonutChartProps {
  data: ChartDatum[];
  /** Pixel size of the SVG square. */
  size?: number;
  /** Title shown in the center (e.g. total). */
  centerLabel?: string;
  /** Sub-label under the center label. */
  centerHint?: string;
  className?: string;
}

export function DonutChart({
  data,
  size = 160,
  centerLabel,
  centerHint,
  className,
}: DonutChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const strokeWidth = size / 7;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  if (total === 0) {
    return (
      <div
        className={cn("flex items-center justify-center text-ink-400 text-sm", className)}
        style={{ width: size, height: size }}
      >
        Ingen data
      </div>
    );
  }

  // Vis storste segmentet forst (klokka 12), deretter med klokken. Dette
  // gir en mer naturlig lesefolge og fjerner inntrykk av "rart" oppsett nar
  // datasettet er sterkt skjevt (eks. 88 / 6 / 5).
  const ordered = data
    .map((d, originalIndex) => ({ ...d, originalIndex }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  // Visuelle parametere:
  // - SEGMENT_GAP_PX: liten luft mellom hvert segment sa de er tydelig adskilt
  //   selv ved sma andeler. Hentes fra dash slik at total fortsatt = circumference.
  // - MIN_DASH_PX: minste synlige boyebredde — segmenter under denne grensen
  //   bumpes opp slik at de ikke forsvinner pa 2-3% andel. Legenden viser
  //   eksakt verdi, sa et lite visuelt overshoot er akseptabelt.
  const SEGMENT_GAP_PX = ordered.length > 1 ? 2 : 0;
  const MIN_DASH_PX = 6;

  let cumulative = 0;
  return (
    <div className={cn("relative inline-flex", className)}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        role="img"
        aria-label="Fordeling per kategori"
      >
        <title>Fordeling per kategori</title>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-ink-100"
        />
        {ordered.map((d) => {
          const fraction = d.value / total;
          const rawDash = fraction * circumference;
          const dash = Math.max(rawDash - SEGMENT_GAP_PX, MIN_DASH_PX);
          const gap = Math.max(circumference - dash, 0);
          const offset = -cumulative * circumference;
          cumulative += fraction;
          return (
            <circle
              key={d.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={colorAt(d.originalIndex, d.color)}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      {(centerLabel || centerHint) && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          {centerLabel && (
            <div className="tabular font-semibold text-base text-ink-900">{centerLabel}</div>
          )}
          {centerHint && <div className="mt-0.5 text-ink-500 text-xs">{centerHint}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChartLegend — accompanies any chart
// ---------------------------------------------------------------------------

export interface ChartLegendProps {
  data: ChartDatum[];
  /** Show the value next to each label. */
  showValue?: boolean;
  /** Show the percent of total next to each label. */
  showPercent?: boolean;
  currency?: string;
  className?: string;
}

export function ChartLegend({
  data,
  showValue = true,
  showPercent,
  currency = "NOK",
  className,
}: ChartLegendProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  return (
    <ul className={cn("space-y-1.5 text-sm", className)}>
      {data.map((d, i) => (
        <li key={d.label} className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: colorAt(i, d.color) }}
            />
            <span className="truncate text-ink-700">{d.label}</span>
          </div>
          <div className="tabular flex shrink-0 items-center gap-2">
            {showValue && (
              <span className="font-medium text-ink-800">{formatCurrency(d.value, currency)}</span>
            )}
            {showPercent && total > 0 && (
              <span className="text-ink-500 text-xs">{formatPercent(d.value / total)}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// HorizontalBars — comparison of values, sorted high to low
// ---------------------------------------------------------------------------

export interface HorizontalBarsProps {
  data: ChartDatum[];
  /** Hide values smaller than this fraction of the max. */
  minFraction?: number;
  currency?: string;
  className?: string;
}

export function HorizontalBars({ data, currency = "NOK", className }: HorizontalBarsProps) {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const max = Math.max(...sorted.map((d) => d.value), 0);

  if (max === 0) {
    return <p className="text-ink-400 text-sm">Ingen data</p>;
  }

  return (
    <ul className={cn("space-y-2", className)}>
      {sorted.map((d, i) => {
        const fraction = max === 0 ? 0 : d.value / max;
        return (
          <li key={d.label}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="truncate text-ink-700">{d.label}</span>
              <span className="tabular ml-2 font-medium text-ink-800">
                {formatCurrency(d.value, currency)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-ink-100">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${fraction * 100}%`,
                  backgroundColor: colorAt(i, d.color),
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
